import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Check, Copy, Eye, Loader2, Printer, Handshake, Clock, Sparkles } from "lucide-react";

const PERKS = [
  { icon: Sparkles, t: "A bonus for your couples", d: "Candid, same-day images and social clips — an extra no other planner is offering." },
  { icon: Eye, t: "Zero disruption", d: "I'm invisible on the day. I never step on the lead photographer or your timeline." },
  { icon: Clock, t: "Content for you, too", d: "Your styling and venue captured from a photojournalist's eye — great for your own marketing." },
  { icon: Handshake, t: "Credited to you", d: "Your own link and QR card, so every couple you send is tracked back to you." },
];

const field = "w-full bg-obsidian-800 border border-white/12 rounded-lg px-4 py-3 text-sm focus:border-amber-glow/60 outline-none";

function Success({ code }) {
  const link = `${window.location.origin}/incognito?ref=${code}`;
  return (
    <div data-testid="planner-success" className="border border-amber-glow/40 rounded-lg p-8 bg-obsidian-800 grid sm:grid-cols-[auto,1fr] gap-8 items-center">
      <div className="bg-white p-3 rounded-md w-fit"><QRCodeSVG value={link} size={150} /></div>
      <div>
        <div className="flex items-center gap-2 text-amber-glow mb-2"><Check size={18} /> You're in</div>
        <div className="font-serif text-2xl font-light mb-3">Your personal Incognito link</div>
        <code data-testid="planner-link" className="block text-sm text-neutral-300 break-all">{link}</code>
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="planner-copy-link" onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }}
            className="border border-white/20 rounded-full px-4 py-2 text-sm flex items-center gap-2 hover:border-amber-glow"><Copy size={14} /> Copy link</button>
          <Link to={`/card?ref=${code}`} data-testid="planner-print-cards" className="btn-amber rounded-full px-4 py-2 text-sm flex items-center gap-2"><Printer size={14} /> Print my QR cards</Link>
        </div>
      </div>
    </div>
  );
}

export default function Planners() {
  const [form, setForm] = useState({ name: "", business: "", role: "planner", email: "", phone: "", city: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState(null);
  useEffect(() => { document.title = "For planners & venues — Incognito Paparazzi"; }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try { const { data } = await api.post("/planners/signup", form); setCode(data.code); }
    catch { toast.error("Something went wrong — please check your email and try again."); }
    setSending(false);
  };

  return (
    <div data-testid="planners-page" className="pt-28">
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-20">
        <div className="overline-label mb-4 text-amber-glow flex items-center gap-2"><Eye size={14} /> For wedding planners &amp; venues</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02]">Give your couples a story<br className="hidden sm:block" /> no one else can.</h1>
        <p className="mt-6 text-neutral-400 max-w-2xl text-base">
          Incognito Paparazzi is a separate vendor that works alongside your couple's photographer — long lenses, completely candid,
          never in the way. Couples sign up free and decide what it's worth after they see the images. Recommend it, and every couple you send is credited to you.
        </p>
      </section>

      <section className="border-y border-white/8 bg-obsidian-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid gap-px sm:grid-cols-2 lg:grid-cols-4 bg-white/8">
          {PERKS.map(({ icon: Icon, t, d }, i) => (
            <div key={i} data-testid={`planner-perk-${i}`} className="bg-obsidian-800 p-8">
              <Icon className="text-amber-glow mb-5" size={24} />
              <div className="font-serif text-xl font-light mb-2">{t}</div>
              <p className="text-neutral-400 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-10">
          <div className="overline-label mb-3">Become a referral partner</div>
          <h2 className="font-serif text-4xl font-light">Get your link &amp; QR cards</h2>
          <p className="mt-3 text-neutral-400 text-sm">Free. You'll get a personal link and printable QR cards right away.</p>
        </div>
        {code ? <Success code={code} /> : (
          <form onSubmit={submit} className="space-y-4" data-testid="planner-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="planner-name" required placeholder="Your name" value={form.name} onChange={set("name")} className={field} />
              <input data-testid="planner-business" required placeholder="Business / venue name" value={form.business} onChange={set("business")} className={field} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <select data-testid="planner-role" value={form.role} onChange={set("role")} className={field}>
                <option value="planner">Wedding planner</option>
                <option value="venue">Venue</option>
                <option value="vendor">Other wedding vendor</option>
              </select>
              <input data-testid="planner-city" placeholder="City" value={form.city} onChange={set("city")} className={field} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="planner-email" type="email" required placeholder="Email" value={form.email} onChange={set("email")} className={field} />
              <input data-testid="planner-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} />
            </div>
            <textarea data-testid="planner-notes" rows={3} placeholder="Anything I should know? (optional)" value={form.notes} onChange={set("notes")} className={field} />
            <button data-testid="planner-submit" type="submit" disabled={sending}
              className="w-full btn-amber rounded-full px-6 py-3.5 flex items-center justify-center gap-2 disabled:opacity-60">
              {sending && <Loader2 size={16} className="animate-spin" />} Become a referral partner
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
